import React, { useState, useEffect, useContext } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../UserContext";
import { impersonationService } from "../../services/impersonationService";

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [coachName, setCoachName] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chatData, setChatData] = useState(null);
  const [chatDate, setChatDate] = useState("");
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
  const { setUser } = useContext(UserContext);

  // Utility function to format date/time in admin's local timezone
  const formatToLocalTime = (serverTimeString) => {
    // Debug: Log the original server time
    // console.log("Original server time:", serverTimeString);

    // Handle different server time formats
    let date;
    if (serverTimeString.includes("T") && serverTimeString.includes("Z")) {
      // ISO format with Z (UTC): "2024-01-15T10:30:00.000Z"
      date = new Date(serverTimeString);
    } else if (serverTimeString.includes("T")) {
      // ISO format without Z: "2024-01-15T10:30:00"
      date = new Date(serverTimeString + "Z"); // Add Z to treat as UTC
    } else {
      // Other formats, try to parse directly
      date = new Date(serverTimeString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", serverTimeString);
      return "Invalid Date";
    }

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const localTime = date.toLocaleString(undefined, options);
    console.log("Converted to local time:", localTime);
    return localTime;
  };

  // Utility function for date grouping (date only)
  const formatToLocalDate = (serverTimeString) => {
    // Handle different server time formats similar to above
    let date;
    if (serverTimeString.includes("T") && serverTimeString.includes("Z")) {
      date = new Date(serverTimeString);
    } else if (serverTimeString.includes("T")) {
      date = new Date(serverTimeString + "Z");
    } else {
      date = new Date(serverTimeString);
    }

    if (isNaN(date.getTime())) {
      console.error("Invalid date for grouping:", serverTimeString);
      return "Invalid Date";
    }

    return date.toLocaleDateString();
  };

  // Fetch all users on load and sort alphabetically
  useEffect(() => {
    if (
      user.user_role === "ac9be322-db89-4beb-854a-46f2c91de374" ||
      user.user_role === "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef"
    ) {
      console.log("you are admin", user.user_id);
      const fetchUsers = async () => {
        try {
          const response = await fetch(`${apiBaseURL}/users/allUsers`);

          if (response.ok) {
            const data = await response.json();
            // Sort users alphabetically by first and last name
            const sortedUsers = data.sort((a, b) =>
              `${a.first_name} ${a.last_name}`.localeCompare(
                `${b.first_name} ${b.last_name}`
              )
            );
            setUsers(sortedUsers);
          } else {
            console.error("Failed to fetch users");
          }
        } catch (error) {
          console.error("Error fetching users:", error);
        }
      };
      fetchUsers();
    } else {
      navigate("/");
      console.log("you are not admin");
    }
  }, [apiBaseURL, navigate, user.user_role, user.user_id]);

  // Filtered users based on search term
  const filteredUsers = users.filter((user) =>
    `${user.first_name} ${user.last_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Fetch sessions for selected user and sort by created_at in descending order
  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedUserId) return;
      try {
        const response = await fetch(
          `${apiBaseURL}/users/${selectedUserId}/allsessions`
        );
        if (response.ok) {
          const data = await response.json();
          // Sort sessions by created_at in descending order (latest to oldest)
          const sortedSessions = data.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          console.log(sortedSessions);
          setSessions(sortedSessions);
          setFilteredSessions(sortedSessions);
        } else {
          console.error("Failed to fetch sessions");
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };
    fetchSessions();
  }, [selectedUserId, apiBaseURL]);

  // Fetch chat data for selected session
  useEffect(() => {
    const fetchChatData = async () => {
      if (!selectedSessionId) return;
      try {
        const response = await fetch(
          `${apiBaseURL}/sessionSteps/${selectedSessionId}`
        );
        if (response.ok) {
          const data = await response.json();
          setChatData(data);

          if (data && data.length > 0) {
            // Debug: Show timezone information
            console.log("=== TIMEZONE DEBUG INFO ===");
            console.log("Server timestamp:", data[0].created_at);
            console.log(
              "Current browser timezone:",
              Intl.DateTimeFormat().resolvedOptions().timeZone
            );
            console.log(
              "Browser timezone offset (minutes):",
              new Date().getTimezoneOffset()
            );

            // Try different parsing methods
            const serverTime = data[0].created_at;
            const directParse = new Date(serverTime);
            const utcParse = new Date(
              serverTime + (serverTime.includes("Z") ? "" : "Z")
            );

            console.log("Direct parse result:", directParse.toString());
            console.log("UTC parse result:", utcParse.toString());
            console.log(
              "Direct parse local string:",
              directParse.toLocaleString()
            );
            console.log("UTC parse local string:", utcParse.toLocaleString());
            console.log("===========================");

            // Format chat date in admin's local timezone
            setChatDate(formatToLocalTime(data[0].created_at));
          }
        } else {
          setChatData(null);
          console.error("Failed to fetch chat data");
        }
      } catch (error) {
        console.error("Error fetching chat data:", error);
      }
    };

    fetchChatData();
  }, [selectedSessionId, apiBaseURL]);

  // Filter sessions based on selected date
  const handleFilterByDate = () => {
    if (!startDate || !endDate) {
      setFilteredSessions(sessions);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const filtered = sessions.filter((session) => {
      const sessionDate = new Date(session.created_at);
      return sessionDate >= start && sessionDate <= end;
    });

    setFilteredSessions(filtered);
  };

  // Function to download the chat session as an Excel file
  const downloadSessionAsExcel = () => {
    const excelData = chatData.map((row, index) => ({
      Step_Number: row.step_number,
      Coachee_Response: row.coachee_response,
      User_Tags: row.user_tags,
      Intention: row.intention,
      Technique: row.technique,
      Coach_Response: row.coach_response,
      Created_At: formatToLocalTime(row.created_at), // Use local time in Excel
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chat History");

    // Use local date for filename
    const localDate = formatToLocalDate(chatData[0].created_at).replace(
      /\//g,
      "-"
    );
    XLSX.writeFile(workbook, `${selectedUserName}_${localDate}.xlsx`);
  };

  // Group sessions by date using admin's local timezone
  const groupSessionsByDate = (sessions) => {
    return sessions.reduce((acc, session) => {
      const date = formatToLocalDate(session.created_at); // Use admin's local timezone
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(session);
      return acc;
    }, {});
  };

  const handleStepInAsUser = async (userId) => {
    console.log("Stepping in as user:", userId);
    const reason = window.prompt(
      "Reason for impersonating this user? Include a support ticket if available."
    );
    if (!reason || reason.trim().length < 3) {
      return;
    }

    const ticketId = window.prompt("Support ticket ID (optional):") || "";
    const payload = {
      reason: reason.trim(),
      ticket_id: ticketId.trim() || undefined,
    };

    try {
      const data = await impersonationService.impersonateUser(userId, payload);
      console.log("This is the data", data);
      impersonationService.storeImpersonatedSession(user, data);
      setUser(data);
      navigate("/");
    } catch (error) {
      console.error("Error impersonating user:", error);
      alert(
        error?.response?.data?.detail ||
          error?.message ||
          "Failed to impersonate user."
      );
    }
  };

  const groupedSessions = groupSessionsByDate(filteredSessions);

  return (
    <div className="p-4 md:p-6 w-full min-h-screen font-gotham bg-gray-50">
      <div className="flex flex-col md:flex-row w-full h-[90vh] bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Left Panel */}
        <div className="md:w-[32%] flex flex-col p-4 border-r border-gray-100 bg-white overflow-y-auto">
          {selectedUserId ? (
            <div className="space-y-4">
              <button
                className="flex items-center text-md text-[#54B0AF] hover:text-[#48998C] transition-colors"
                onClick={() => {
                  setSelectedUserId(null);
                  setSelectedUserName(null);
                  setSelectedSessionId(null);
                  setCoachName(null);
                  setChatData(null);
                  setSessions([]);
                  setFilteredSessions([]);
                }}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Users
              </button>

              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-700">
                  {selectedUserName}'s Sessions
                </h2>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-md bg-gray-50 rounded-lg border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#54B0AF]"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 text-md bg-gray-50 rounded-lg border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#54B0AF]"
                  />
                </div>
                <button
                  onClick={handleFilterByDate}
                  className="w-full px-4 py-2 text-md font-medium bg-[#54B0AF] text-white rounded-lg hover:bg-[#48998C] transition-colors"
                >
                  Filter Sessions
                </button>
              </div>

              <div className="space-y-4">
                {Object.keys(groupedSessions).map((date) => (
                  <div key={date} className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-400 tracking-wide">
                      {date}
                    </h3>
                    {groupedSessions[date].map((session) => (
                      <div
                        key={session.session_id}
                        onClick={() => {
                          setSelectedSessionId(session.session_id);
                          setCoachName(session.coach_name);
                        }}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedSessionId === session.session_id
                            ? "bg-[#54B0AF]/10 ring-1 ring-[#54B0AF]/20"
                            : "bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <p className="text-md text-gray-700 line-clamp-2">
                          {session.coachee_response}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          {session.coach_name}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-4 px-4 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />

              {filteredUsers.map((selected_user) => (
                <div
                  key={selected_user.userid}
                  onClick={() => {
                    setSelectedUserId(selected_user.userid);
                    setSelectedUserName(selected_user.first_name);
                  }}
                  className="relative group p-3 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <p className="text-md font-medium text-gray-700">
                    {selected_user.first_name} {selected_user.last_name}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {selected_user.email}
                  </p>

                  {user.user_role ===
                    "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStepInAsUser(selected_user.userid);
                      }}
                      className="absolute top-3 right-3 hidden group-hover:block bg-teal-500 hover:bg-teal-600 text-white text-sm px-3 py-1 rounded-md shadow-md transition-all"
                    >
                      Step in as user
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="md:w-[68%] flex flex-col bg-white">
          {selectedSessionId ? (
            <>
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div>
                  <h2 className="text-lg font-medium text-gray-700">
                    {coachName}
                  </h2>
                  <p className="text-md text-gray-400">{chatDate}</p>
                </div>
                <button
                  onClick={downloadSessionAsExcel}
                  className="px-4 py-2 text-md font-medium bg-[#54B0AF] text-white rounded-lg hover:bg-[#48998C] transition-colors"
                >
                  Export Session
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {chatData?.map((message, index) => (
                  <div key={index} className="space-y-4">
                    {/* User Message */}
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="max-w-[85%] p-3 rounded-lg bg-gray-200">
                          <p className="text-md text-gray-800">
                            {message.coachee_response}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Coach Response */}
                    <div
                      className="flex items-start gap-3 flex-row-reverse"
                      title={
                        message.predictions
                          ? `Step number: ${
                              message.step_number
                            }\n${message.predictions
                              .split(",")
                              .join("\n")}\n Step explanation: ${
                              message.explanations
                            }`
                          : `Step number: ${message.step_number}`
                      }
                    >
                      <div className="flex-1">
                        <div className="max-w-[85%] ml-auto p-3 rounded-lg bg-gray-100">
                          {message.modified_response ? (
                            <>
                              <p className="text-md text-gray-400 line-through">
                                {message.initial_response}
                              </p>
                              <p className="text-md text-gray-700 mt-1">
                                {message.modified_response}
                              </p>
                            </>
                          ) : (
                            <p className="text-md text-gray-700">
                              {message.coach_response}
                            </p>
                          )}
                          <div className="mt-2 text-sm text-gray-500 space-y-1">
                            <p>Technique: {message.technique}</p>
                            <p>Intention: {message.intention}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              Select a session to view conversation
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;

// import React, { useState, useEffect, useContext } from "react";
// import * as XLSX from "xlsx";
// import { useNavigate } from "react-router-dom";
// import { UserContext } from "../../UserContext";

// const AdminPage = () => {
//   const [users, setUsers] = useState([]);
//   const [sessions, setSessions] = useState([]);
//   const [filteredSessions, setFilteredSessions] = useState([]);
//   const [selectedUserId, setSelectedUserId] = useState(null);
//   const [selectedUserName, setSelectedUserName] = useState(null);
//   const [selectedSessionId, setSelectedSessionId] = useState(null);
//   const [coachName, setCoachName] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [chatData, setChatData] = useState(null);
//   const [chatDate, setChatDate] = useState("");
//   const navigate = useNavigate();
//   const { user } = useContext(UserContext);
//   const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
//   const { updateUserFromObject } = useContext(UserContext);

//   // Fetch all users on load and sort alphabetically
//   useEffect(() => {
//     if (
//       user.user_role === "ac9be322-db89-4beb-854a-46f2c91de374" ||
//       user.user_role === "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef"
//     ) {
//       console.log("you are admin", user.user_id);
//       const fetchUsers = async () => {
//         try {
//           const response = await fetch(`${apiBaseURL}/users/allUsers`);

//           if (response.ok) {
//             const data = await response.json();
//             // Sort users alphabetically by first and last name
//             const sortedUsers = data.sort((a, b) =>
//               `${a.first_name} ${a.last_name}`.localeCompare(
//                 `${b.first_name} ${b.last_name}`
//               )
//             );
//             setUsers(sortedUsers);
//           } else {
//             console.error("Failed to fetch users");
//           }
//         } catch (error) {
//           console.error("Error fetching users:", error);
//         }
//       };
//       fetchUsers();
//     } else {
//       navigate("/");
//       console.log("you are not admin");
//     }
//   }, []);

//   // Filtered users based on search term
//   const filteredUsers = users.filter((user) =>
//     `${user.first_name} ${user.last_name}`
//       .toLowerCase()
//       .includes(searchTerm.toLowerCase())
//   );

//   // Fetch sessions for selected user and sort by created_at in descending order
//   useEffect(() => {
//     const fetchSessions = async () => {
//       if (!selectedUserId) return;
//       try {
//         const response = await fetch(
//           `${apiBaseURL}/users/${selectedUserId}/allsessions`
//         );
//         if (response.ok) {
//           const data = await response.json();
//           // Sort sessions by created_at in descending order (latest to oldest)
//           const sortedSessions = data.sort(
//             (a, b) => new Date(b.created_at) - new Date(a.created_at)
//           );
//           console.log(sortedSessions);
//           setSessions(sortedSessions);
//           setFilteredSessions(sortedSessions);
//         } else {
//           console.error("Failed to fetch sessions");
//         }
//       } catch (error) {
//         console.error("Error fetching sessions:", error);
//       }
//     };
//     fetchSessions();
//   }, [selectedUserId]);

//   // Fetch chat data for selected session
//   useEffect(() => {
//     const fetchChatData = async () => {
//       if (!selectedSessionId) return;
//       try {
//         const response = await fetch(
//           `${apiBaseURL}/sessionSteps/${selectedSessionId}`
//         );
//         if (response.ok) {
//           const data = await response.json();
//           setChatData(data);
//           const createdAt = new Date(data[0].created_at); // Parse the server time (UTC)

//           // Format the date and time in the user's local timezone
//           // const date = createdAt.toLocaleDateString([], {
//           //   weekday: "long",
//           //   year: "numeric",
//           //   month: "long",
//           //   day: "numeric",
//           // });
//           const date = new Date(data[0].created_at).toDateString();
//           const time = createdAt.toLocaleTimeString([], {
//             hour: "2-digit",
//             minute: "2-digit",
//             hour12: true, // Optional: Use 12-hour format; omit for 24-hour
//           });

//           setChatDate(`${date} at ${time}`);
//           console.log(data);
//         } else {
//           setChatData(null);
//           console.error("Failed to fetch chat data");
//         }
//       } catch (error) {
//         console.error("Error fetching chat data:", error);
//       }
//     };
//     fetchChatData();
//   }, [selectedSessionId]);

//   // Filter sessions based on selected date
//   const handleFilterByDate = () => {
//     if (!startDate || !endDate) {
//       setFilteredSessions(sessions);
//       return;
//     }

//     const start = new Date(startDate);
//     const end = new Date(endDate);
//     end.setHours(23, 59, 59, 999);

//     const filtered = sessions.filter((session) => {
//       const sessionDate = new Date(session.created_at);
//       return sessionDate >= start && sessionDate <= end;
//     });

//     setFilteredSessions(filtered);
//   };

//   // Function to download the chat session as an Excel file
//   const downloadSessionAsExcel = () => {
//     const excelData = chatData.map((row, index) => ({
//       Step_Number: row.step_number,
//       Coachee_Response: row.coachee_response,
//       User_Tags: row.user_tags,
//       Intention: row.intention,
//       Technique: row.technique,
//       Coach_Response: row.coach_response,
//       Created_At: new Date(row.created_at).toLocaleString(),
//     }));

//     const worksheet = XLSX.utils.json_to_sheet(excelData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Chat History");

//     XLSX.writeFile(
//       workbook,
//       `${selectedUserName}_${chatData[0].created_at}.xlsx`
//     );
//   };

//   // Group sessions by date
//   const groupSessionsByDate = (sessions) => {
//     return sessions.reduce((acc, session) => {
//       const date = new Date(session.created_at).toLocaleDateString(); // Format date to MM/DD/YYYY
//       if (!acc[date]) {
//         acc[date] = [];
//       }
//       acc[date].push(session);
//       return acc;
//     }, {});
//   };

//   const handleStepInAsUser = async (userId) => {
//     console.log("Stepping in as user:", userId);
//     const payload = {
//       admin_id: user.user_id,
//       user_id: userId,
//     };
//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_BACKEND_API_URL}/admin/users/${userId}/impersonate`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }
//       );
//       const data = await response.json();
//       console.log("This is the data", data);
//       if (response.ok) {
//         console.log("sucess fully appendeed");
//         updateUserFromObject(data);
//         navigate("/");
//       }
//     } catch (error) {
//       console.error("Error creating session:", error);
//     }
//     // Your custom logic here
//   };

//   const groupedSessions = groupSessionsByDate(filteredSessions);

//   return (
//     <div className="p-4 md:p-6 w-full min-h-screen font-gotham bg-gray-50">
//       <div className="flex flex-col md:flex-row w-full h-[90vh] bg-white rounded-xl shadow-lg overflow-hidden">
//         {/* Left Panel */}
//         <div className="md:w-[32%] flex flex-col p-4 border-r border-gray-100 bg-white overflow-y-auto">
//           {selectedUserId ? (
//             <div className="space-y-4">
//               <button
//                 className="flex items-center text-md text-[#54B0AF] hover:text-[#48998C] transition-colors"
//                 onClick={() => {
//                   setSelectedUserId(null);
//                   setSelectedUserName(null);
//                   setSelectedSessionId(null);
//                   setCoachName(null);
//                   setChatData(null);
//                   setSessions([]);
//                   setFilteredSessions([]);
//                 }}
//               >
//                 <svg
//                   className="w-4 h-4 mr-1"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth="2"
//                     d="M15 19l-7-7 7-7"
//                   />
//                 </svg>
//                 Back to Users
//               </button>

//               <div className="space-y-4">
//                 <h2 className="text-lg font-medium text-gray-700">
//                   {selectedUserName}'s Sessions
//                 </h2>
//                 <div className="flex gap-2">
//                   <input
//                     type="date"
//                     value={startDate}
//                     onChange={(e) => setStartDate(e.target.value)}
//                     className="w-full px-3 py-2 text-md bg-gray-50 rounded-lg border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#54B0AF]"
//                   />
//                   <input
//                     type="date"
//                     value={endDate}
//                     onChange={(e) => setEndDate(e.target.value)}
//                     className="w-full px-3 py-2 text-md bg-gray-50 rounded-lg border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#54B0AF]"
//                   />
//                 </div>
//                 <button
//                   onClick={handleFilterByDate}
//                   className="w-full px-4 py-2 text-md font-medium bg-[#54B0AF] text-white rounded-lg hover:bg-[#48998C] transition-colors"
//                 >
//                   Filter Sessions
//                 </button>
//               </div>

//               <div className="space-y-4">
//                 {Object.keys(groupedSessions).map((date) => (
//                   <div key={date} className="space-y-2">
//                     <h3 className="text-sm font-medium text-gray-400 tracking-wide">
//                       {date}
//                     </h3>
//                     {groupedSessions[date].map((session) => (
//                       <div
//                         key={session.session_id}
//                         onClick={() => {
//                           setSelectedSessionId(session.session_id);
//                           setCoachName(session.coach_name);
//                         }}
//                         className={`p-3 rounded-lg cursor-pointer transition-colors ${
//                           selectedSessionId === session.session_id
//                             ? "bg-[#54B0AF]/10 ring-1 ring-[#54B0AF]/20"
//                             : "bg-gray-50 hover:bg-gray-100"
//                         }`}
//                       >
//                         <p className="text-md text-gray-700 line-clamp-2">
//                           {session.coachee_response}
//                         </p>
//                         <p className="text-sm text-gray-400 mt-1">
//                           {session.coach_name}
//                         </p>
//                       </div>
//                     ))}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           ) : (
//             <div className="space-y-2">
//               <input
//                 type="text"
//                 placeholder="Search by name..."
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="mb-4 px-4 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
//               />

//               {filteredUsers.map((selected_user) => (
//                 <div
//                   key={selected_user.userid}
//                   onClick={() => {
//                     setSelectedUserId(selected_user.userid);
//                     setSelectedUserName(selected_user.first_name);
//                   }}
//                   className="relative group p-3 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
//                 >
//                   <p className="text-md font-medium text-gray-700">
//                     {selected_user.first_name} {selected_user.last_name}
//                   </p>
//                   <p className="text-sm text-gray-400 mt-1">
//                     {selected_user.email}
//                   </p>

//                   {user.user_role ===
//                     "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef" && (
//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation(); // prevent div's onClick from firing
//                         handleStepInAsUser(selected_user.userid); // call your custom function
//                       }}
//                       className="absolute top-3 right-3 hidden group-hover:block bg-teal-500 hover:bg-teal-600 text-white text-sm px-3 py-1 rounded-md shadow-md transition-all"
//                     >
//                       Step in as user
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Right Panel */}
//         <div className="md:w-[68%] flex flex-col bg-white">
//           {selectedSessionId ? (
//             <>
//               <div className="flex items-center justify-between p-4 border-b border-gray-100">
//                 <div>
//                   <h2 className="text-lg font-medium text-gray-700">
//                     {coachName}
//                   </h2>
//                   <p className="text-md text-gray-400">{chatDate}</p>
//                   {/* <p className="text-md text-gray-400">
//                     {new Date(chatDate).toLocaleString()}
//                   </p> */}
//                 </div>
//                 <button
//                   onClick={downloadSessionAsExcel}
//                   className="px-4 py-2 text-md font-medium bg-[#54B0AF] text-white rounded-lg hover:bg-[#48998C] transition-colors"
//                 >
//                   Export Session
//                 </button>
//               </div>

//               <div className="flex-1 overflow-y-auto p-4 space-y-6">
//                 {chatData?.map((message, index) => (
//                   <div key={index} className="space-y-4">
//                     {/* User Message */}
//                     <div className="flex items-start gap-3">
//                       <div className="flex-1">
//                         <div className="max-w-[85%] p-3 rounded-lg bg-gray-200">
//                           <p className="text-md text-gray-800">
//                             {message.coachee_response}
//                           </p>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Coach Response */}
//                     <div
//                       className="flex items-start gap-3 flex-row-reverse"
//                       title={
//                         message.predictions
//                           ? `Step number: ${
//                               message.step_number
//                             }\n${message.predictions
//                               .split(",")
//                               .join("\n")}\n Step explanation: ${
//                               message.explanations
//                             }`
//                           : `Step number: ${message.step_number}`
//                       }
//                     >
//                       <div className="flex-1">
//                         <div className="max-w-[85%] ml-auto p-3 rounded-lg bg-gray-100">
//                           {message.modified_response ? (
//                             <>
//                               <p className="text-md text-gray-400 line-through">
//                                 {message.initial_response}
//                               </p>
//                               <p className="text-md text-gray-700 mt-1">
//                                 {message.modified_response}
//                               </p>
//                             </>
//                           ) : (
//                             <p className="text-md text-gray-700">
//                               {message.coach_response}
//                             </p>
//                           )}
//                           <div className="mt-2 text-sm text-gray-500 space-y-1">
//                             <p>Technique: {message.technique}</p>
//                             <p>Intention: {message.intention}</p>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </>
//           ) : (
//             <div className="flex-1 flex items-center justify-center text-gray-400">
//               Select a session to view conversation
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AdminPage;
